import React, { useContext } from "react";
import { Autocomplete, FormControl, TextField, useTheme } from "@mui/material";
import { getJourneyStatusesResponse } from "@/modules/common/dtos/index.v2";
import { userHasPermission } from "@/modules/common/utils";
import { PermissionsEnum, PermissionLevel } from "@/modules/common/types";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { SalesStatuses } from "@/modules/sales/constants";
import { normalizeStatusName } from "@/modules/lead-page/utils/status-name";
import Color from "color";

interface StatusSelectorProps {
  data: getJourneyStatusesResponse[];
  handleChangeStatus: (event: React.ChangeEvent<{ value: unknown }>) => void;
  setStatusFormUId: (id: string | null) => void;
}

const StatusSelector: React.FC<StatusSelectorProps> = ({
  data = [],
  handleChangeStatus,
  setStatusFormUId,
}) => {
  const theme = useTheme();
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);
  if (!Array.isArray(data) || data.length === 0) {
    return <TextField label="Status" disabled />;
  }

  const canCreateSale = userHasPermission(
    { name: PermissionsEnum.CAN_CREATE_SALE, level: PermissionLevel.WRITE },
    currentUserPermissions
  );

  // Filter out statuses without categories
  const statusesWithCategories = data.filter(
    (status) => status.statusCategory && status.statusCategory.name
  );

  const restrictedStatusNames = [
    SalesStatuses.CONGRATS_ITS_A_SALE,
    SalesStatuses.UNLOCKED_CONGRATS,
    SalesStatuses.UNIT_RESERVED,
  ].map(normalizeStatusName);

  const filteredOptions = !canCreateSale
    ? statusesWithCategories.filter(
        (status) =>
          !restrictedStatusNames.includes(normalizeStatusName(status.name))
      )
    : statusesWithCategories;

  return (
    <FormControl
      data-testid="edit-status-select"
      sx={{ width: "100%", minHeight: "fit-content" }}
    >
      <Autocomplete
        onChange={(_, newValue) => {
          handleChangeStatus({
            target: { value: newValue || null },
          } as React.ChangeEvent<{ value: unknown }>);
          setStatusFormUId(newValue?.form?.uid || null);
        }}
        options={filteredOptions}
        getOptionLabel={(option) => option.name || ""}
        renderInput={(params) => <TextField {...params} label="Status" />}
        renderOption={(props, option) => {
          const colorObj = Color(option.color || "#ffffff");
          const backgroundColor = colorObj.alpha(0.2).toString();
          const textColor = option.color
            ? colorObj.darken(0.5).toString()
            : "#000000";

          return (
            <li
              {...props}
              key={option.uid}
              style={{
                backgroundColor: backgroundColor,
                color: textColor,
                fontSize:
                  theme.nawy.typography.english.variants.body.md.regular
                    .fontSize,
                fontWeight: "bold",
              }}
            >
              {option.name}
            </li>
          );
        }}
        isOptionEqualToValue={(option, value) => option.uid === value?.uid}
        sx={{ width: "100%" }}
      />
    </FormControl>
  );
};

export default StatusSelector;
